import type { OccupancySeasonHeatmapCell, OccupancySpanKind } from '@sea-snag/shared';

/**
 * What one grid cell is, and how it looks.
 *
 * One solid colour per cell, and a partial week is coloured exactly like a full
 * one. Booked cells used to carry three emerald opacities keyed on day count,
 * which shaded a two-day charter lighter than a seven-day one — a proportional
 * fill by another name, and the brief rules those out: the week either held a
 * charter or it did not. How much of it was sold belongs in the cell detail,
 * where the exact day count and dates are, not in a shade the eye has to judge.
 *
 * Precedence decides mixed weeks. It matches the span ordering in the detail, so
 * the first booking listed is always the one the colour came from.
 */

export type CellKind = OccupancySpanKind | 'free';

export function cellKind(cell: OccupancySeasonHeatmapCell | undefined): CellKind {
  if (!cell) return 'free';
  // Precedence matches the day buckets, so the cell colour and the booking
  // listed first in its detail popover always agree.
  if (cell.regattaDays > 0) return 'regatta';
  if (cell.bookedDays > 0) return 'booked';
  if (cell.ownerDays > 0) return 'owner';
  if (cell.externalDays > 0) return 'external';
  if (cell.optionDays > 0) return 'option';
  if (cell.serviceDays > 0) return 'service';
  if (cell.otherDays > 0) return 'other';
  return 'free';
}

/**
 * One class per kind, no opacity modifiers.
 *
 * Brand chart tokens rather than literal purple/yellow/green, per @steffsplit-hub
 * on #211, with stark contrast between a charter, an option and an owner week —
 * the three an operator has to tell apart at a glance.
 *
 * The one departure from the chart ramp is the option. Every chart token is hue
 * 230, so charter, owner, option and service came out as four washes of the same
 * blue, and the option — the only one of them that is *not* sold time — was the
 * easiest to mistake for a booking. It uses a warm light yellow instead, which
 * reads as "held" against the blues while staying within the palette.
 *
 * A regatta gets its own teal. It stays on the cool side of the palette because
 * the week *was* sold, but it no longer shares the charter's fill — that left a
 * 5px corner marker as the only thing separating the two, which is more than a
 * marker that size can carry on a 62px cell. The marker is kept as well, so hue
 * is not the only signal.
 *
 * Each kind carries its own text colour. The grid used to hard-code white on
 * every non-free cell, which silently ruled out any light fill — the cells print
 * prices, and white on light yellow is unreadable.
 */
export const CELL_STYLES: Record<
  CellKind,
  { className: string; textClassName: string; label: string }
> = {
  booked: {
    className: 'bg-[var(--chart-1)]',
    textClassName: 'text-white',
    label: 'Confirmed charter',
  },
  regatta: {
    className: 'bg-[var(--occupancy-regatta)]',
    textClassName: 'text-white',
    label: 'Regatta',
  },
  owner: { className: 'bg-[var(--chart-2)]', textClassName: 'text-white', label: 'Owner week' },
  option: {
    className: 'bg-[var(--occupancy-option)]',
    // Dark text: the fill is light in both themes, so this is fixed rather than
    // theme-dependent.
    textClassName: 'text-slate-900',
    label: 'Option',
  },
  service: { className: 'bg-[var(--chart-5)]', textClassName: 'text-white', label: 'Service' },
  // Blocked by a status MMK has not explained — Offer or Custom today. Neutral
  // on purpose: a boat that is taken must not render as sellable.
  other: {
    className: 'bg-muted-foreground',
    textClassName: 'text-white',
    label: 'Other booking',
  },
  // Calendar blocks a week no reservation of ours explains. Now rare: what used
  // to land here were owner weeks and regattas excluded by a status filter.
  external: {
    className: 'bg-slate-400',
    textClassName: 'text-white',
    label: 'Blocked, unexplained',
  },
  free: { className: 'bg-muted', textClassName: 'text-muted-foreground', label: 'Free' },
};

/**
 * A kind's style, and Other's for a kind this build doesn't know. A page left
 * open across a deploy can be sent a kind added since it loaded; on 9 Oct
 * reading the missing style took Occupancy down for a user mid-session.
 */
export function cellStyle(kind: string): (typeof CELL_STYLES)[CellKind] {
  return Object.hasOwn(CELL_STYLES, kind) ? CELL_STYLES[kind as CellKind] : CELL_STYLES.other;
}

/** Legend order, busiest-to-quietest. Regatta rides with the charter colour. */
export const LEGEND_ORDER: CellKind[] = [
  'booked',
  'regatta',
  'owner',
  'option',
  'service',
  'other',
  'external',
  'free',
];

/** Kinds that carry a corner marker on top of their own colour. */
export const MARKED_KINDS: ReadonlySet<CellKind> = new Set<CellKind>(['regatta']);
